import { useCallback, useEffect, useState } from "react";
import { Button, Form, Input, message, Modal, Space, Spin, Tag, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import intl from "react-intl-universal";
import {
	bindOgeAccount,
	getOgeAccount,
	unbindOgeAccount,
	updateOgeAccount,
} from "@/apis/user";
import {
	createOgeAccountPayload,
	PASSWORD_ENCRYPTION_FAILED,
} from "./ogeAccountUtils";
import styles from "./ogeAccount.module.scss";

const getStatusDisplay = (status) => {
	const statusMap = {
		UNVERIFIED: {
			color: "orange",
			text: intl.get("OgeAccount.Unverified"),
		},
		ACTIVE: {
			color: "green",
			text: intl.get("OgeAccount.Active"),
		},
		INVALID: {
			color: "red",
			text: intl.get("OgeAccount.Invalid"),
		},
	};

	return statusMap[status] || {
		color: "default",
		text: status || intl.get("OgeAccount.Unknown status"),
	};
};

const OgeAccount = () => {
	const [form] = Form.useForm();
	const [account, setAccount] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadFailed, setLoadFailed] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const loadAccount = useCallback(async () => {
		setLoading(true);
		setLoadFailed(false);
		try {
			const response = await getOgeAccount();
			setAccount(response.data);
		} catch (error) {
			setLoadFailed(true);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadAccount();
	}, [loadAccount]);

	const openAccountModal = () => {
		form.setFieldsValue({
			ogeUsername: account?.bound ? account.ogeUsername : undefined,
			password: undefined,
		});
		setModalOpen(true);
	};

	const closeAccountModal = () => {
		form.resetFields();
		setModalOpen(false);
	};

	const submitAccount = async (values) => {
		setSubmitting(true);
		try {
			const requestData = createOgeAccountPayload(values.ogeUsername, values.password);
			if (account?.bound) {
				await updateOgeAccount(requestData);
			} else {
				await bindOgeAccount(requestData);
			}
			message.success(intl.get(account?.bound ? "OgeAccount.Update succeeded" : "OgeAccount.Bind succeeded"));
			closeAccountModal();
			await loadAccount();
		} catch (error) {
			if (error instanceof Error && error.message === PASSWORD_ENCRYPTION_FAILED) {
				message.error(intl.get("OgeAccount.Encryption failed"));
			}
		} finally {
			form.setFieldValue("password", undefined);
			setSubmitting(false);
		}
	};

	const confirmUnbind = () => {
		Modal.confirm({
			title: intl.get("OgeAccount.Unbind confirmation"),
			content: intl.get("OgeAccount.Unbind warning"),
			okText: intl.get("OgeAccount.Unbind"),
			cancelText: intl.get("OgeAccount.Cancel"),
			okButtonProps: { danger: true },
			async onOk() {
				await unbindOgeAccount();
				message.success(intl.get("OgeAccount.Unbind succeeded"));
				form.resetFields();
				await loadAccount();
			},
		});
	};

	const statusDisplay = getStatusDisplay(account?.status);

	return <>
		<div className={styles.accountBox}>
			<div className={styles.header}>
				<strong>{intl.get("OgeAccount.Title")}</strong>
				<Tooltip title={intl.get("OgeAccount.Refresh")}>
					<Button
						type="text"
						size="small"
						aria-label={intl.get("OgeAccount.Refresh")}
						icon={<ReloadOutlined spin={loading} />}
						onClick={loadAccount}
						disabled={loading}
					/>
				</Tooltip>
			</div>
			{loading ? (
				<div className={styles.loading}><Spin size="small" /></div>
			) : loadFailed ? (
				<div className={styles.accountRow}>
					<span>{intl.get("OgeAccount.Load failed")}</span>
					<Button type="link" size="small" onClick={loadAccount}>{intl.get("OgeAccount.Retry")}</Button>
				</div>
			) : account?.bound ? (
				<>
					<div className={styles.accountRow}>
						<Tooltip title={account.ogeUsername}>
							<span className={styles.username}>{account.ogeUsername}</span>
						</Tooltip>
						<Tag color={statusDisplay.color}>{statusDisplay.text}</Tag>
					</div>
					<Space size={4} className={styles.actions}>
						<Button type="link" size="small" onClick={openAccountModal}>{intl.get("OgeAccount.Update")}</Button>
						<Button type="link" size="small" danger onClick={confirmUnbind}>{intl.get("OgeAccount.Unbind")}</Button>
					</Space>
				</>
			) : (
				<div className={styles.accountRow}>
					<span>{intl.get("OgeAccount.Not linked")}</span>
					<Button type="link" size="small" onClick={openAccountModal}>{intl.get("OgeAccount.Add")}</Button>
				</div>
			)}
		</div>

		<Modal
			title={intl.get(account?.bound ? "OgeAccount.Update title" : "OgeAccount.Bind title")}
			open={modalOpen}
			onCancel={closeAccountModal}
			footer={null}
			destroyOnHidden
		>
			<Form form={form} layout="vertical" onFinish={submitAccount} autoComplete="off" preserve={false}>
				<Form.Item
					label={intl.get("OgeAccount.Username")}
					name="ogeUsername"
					rules={[{ required: true, whitespace: true, message: intl.get("OgeAccount.Username required") }]}
				>
					<Input maxLength={100} autoComplete="off" />
				</Form.Item>
				<Form.Item
					label={intl.get("OgeAccount.Password")}
					name="password"
					rules={[{ required: true, message: intl.get("OgeAccount.Password required") }]}
				>
					<Input.Password maxLength={100} autoComplete="new-password" />
				</Form.Item>
				<Space>
					<Button type="primary" htmlType="submit" loading={submitting}>
						{intl.get(account?.bound ? "OgeAccount.Save changes" : "OgeAccount.Bind")}
					</Button>
					<Button onClick={closeAccountModal} disabled={submitting}>{intl.get("OgeAccount.Cancel")}</Button>
				</Space>
			</Form>
		</Modal>
	</>;
};

export default OgeAccount;
